import { useState, useEffect, useCallback, createContext, useContext } from "react";

export interface CookiePreferences {
  necessary: true; // always true, cannot be disabled
  functional: boolean; // Google Maps, etc.
}

interface CookieConsentState {
  hasInteracted: boolean; // user has made a choice
  preferences: CookiePreferences;
}

interface CookieConsentContextValue {
  state: CookieConsentState;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
  resetConsent: () => void;
  isFunctionalAllowed: boolean;
}

const STORAGE_KEY = "armando-cookie-consent-v4";

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functional: false,
};

function loadState(): CookieConsentState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed.hasInteracted === "boolean" && parsed.preferences) {
        return {
          hasInteracted: parsed.hasInteracted,
          preferences: {
            necessary: true,
            functional: !!parsed.preferences.functional,
          },
        };
      }
    }
  } catch {
    // ignore parse errors
  }
  return { hasInteracted: false, preferences: defaultPreferences };
}

function persistState(state: CookieConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

export function useCookieConsent() {
  const [state, setState] = useState<CookieConsentState>(loadState);

  useEffect(() => {
    persistState(state);
  }, [state]);

  const acceptAll = useCallback(() => {
    setState({
      hasInteracted: true,
      preferences: { necessary: true, functional: true },
    });
  }, []);

  const rejectAll = useCallback(() => {
    setState({
      hasInteracted: true,
      preferences: { necessary: true, functional: false },
    });
  }, []);

  const savePreferences = useCallback((preferences: CookiePreferences) => {
    setState({
      hasInteracted: true,
      preferences: { ...preferences, necessary: true },
    });
  }, []);

  const resetConsent = useCallback(() => {
    setState({ hasInteracted: false, preferences: defaultPreferences });
  }, []);

  return {
    state,
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    isFunctionalAllowed: state.preferences.functional,
  };
}

// Context for sharing consent state across the app
export const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function useCookieConsentContext() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsentContext must be used within CookieConsentProvider");
  }
  return ctx;
}
